import React from 'react';
import { Box, Typography, Divider, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { QRCodeSVG } from 'qrcode.react';

export interface PassRecord {
  pass_type: string;
  full_name?: string;
  id_number?: string;
  country_of_origin?: string;
  screening_type?: string;
  screening_status?: string;
  action_required?: string;
  next_step?: string;
  issued_at?: string;
  valid_until?: string;
  id?: number;
  screening_id?: number;
  pass_token?: string;
}

interface Props {
  passRecord: PassRecord;
  /** Hide internal EVD ref / never fall back to numeric screening URL */
  publicSafe?: boolean;
}

const fmt = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Africa/Nairobi',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).toUpperCase();
};

const fmtReportId = (id?: number, issuedAt?: string) => {
  if (!id) return null;
  const d = issuedAt ? new Date(issuedAt) : new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Nairobi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '00';
  const seq = String(id).padStart(4, '0');
  return `EVD-${get('year')}${get('month')}${get('day')}-${seq}`;
};

const HealthPassCard: React.FC<Props> = ({ passRecord, publicSafe = false }) => {
  const isCleared  = passRecord.pass_type === 'cleared';
  const accent     = isCleared ? '#1a7a3c' : '#8b0000';
  const accentLight = isCleared ? '#e8f5e9' : '#fce4e4';
  const reportId = publicSafe
    ? null
    : fmtReportId(passRecord.screening_id, passRecord.issued_at);

  let isExpired = false;
  if (passRecord.valid_until) {
    try {
      isExpired = new Date(passRecord.valid_until).getTime() < Date.now();
    } catch { /* ignore */ }
  }

  // Only ever encode the opaque /pass/{token} link (or /screen for the
  // public-safe self-screen entry point) — never fall back to a raw
  // /dashboard/traveler-screening/{id} URL. That route requires staff login,
  // so a QR encoding it wouldn't work for whoever scans it anyway, and
  // printing/sharing an internal dashboard link is the wrong thing to embed
  // regardless. If no token is available yet, show "QR unavailable" instead.
  const qrValue = passRecord.pass_token
    ? `${window.location.origin}/pass/${passRecord.pass_token}`
    : (publicSafe ? `${window.location.origin}/screen` : null);

  const rows: [string, string][] = [
    ['Full Name',             passRecord.full_name          ?? '—'],
    ['Passport Number',       passRecord.id_number          ?? '—'],
    ['Country of Departure',  passRecord.country_of_origin  ?? '—'],
    ['Screening Type',        passRecord.screening_type     ?? 'Ebola Screening'],
    ['Screening Status',      passRecord.screening_status   ?? '—'],
    ...(isCleared
      ? [
          ['Date Issued', fmt(passRecord.issued_at)],
          ['Valid Until',  fmt(passRecord.valid_until)],
        ] as [string, string][]
      : [
          ['Action Required', passRecord.action_required ?? 'Report to health checkpoint'],
          ['Next Step',       passRecord.next_step       ?? 'Await clinician review'],
        ] as [string, string][]
    ),
  ];

  return (
    <Box
      id="health-pass-card"
      sx={{
        border: `2px solid ${isExpired ? '#9a3412' : accent}`,
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: 580,
        mx: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        fontFamily: '"Roboto", sans-serif',
        bgcolor: '#fff',
      }}
    >
      {/* ── Header strip ── */}
      <Box sx={{ bgcolor: isExpired ? '#9a3412' : accent, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, p: 0.5, display: 'flex', alignItems: 'center' }}>
            <img src="/logo/moh_logo.png" alt="MOH" style={{ height: 42, width: 'auto', display: 'block', objectFit: 'contain' }} />
          </Box>
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase' }}>
              Republic of Kenya 🇰🇪
            </Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: 0.5 }}>
              UHAI · Ministry of Health
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {isCleared
            ? <CheckCircleIcon sx={{ color: '#fff', fontSize: 32, opacity: 0.95 }} />
            : <CancelIcon     sx={{ color: '#fff', fontSize: 32, opacity: 0.95 }} />
          }
        </Box>
      </Box>

      {/* ── Sub-header ── */}
      <Box sx={{ bgcolor: isExpired ? '#ffedd5' : accentLight, px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: isExpired ? '#9a3412' : accent, textTransform: 'uppercase', letterSpacing: 0.8, flex: 1 }}>
          Traveller Health Pass — Ebola Screening Certificate
        </Typography>
        {isExpired && (
          <Chip
            label="QR EXPIRED"
            size="small"
            sx={{ bgcolor: '#9a3412', color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5 }}
          />
        )}
        {reportId && !isExpired && (
          <Chip
            label={reportId}
            size="small"
            sx={{ bgcolor: accent, color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5 }}
          />
        )}
      </Box>

      {/* ── Status banner ── */}
      <Box sx={{ bgcolor: isExpired ? '#9a3412' : accent, py: 0.6, textAlign: 'center' }}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.8rem', letterSpacing: 2, textTransform: 'uppercase' }}>
          {isExpired
            ? '✗  QR Code Is Expired — Pass No Longer Valid'
            : isCleared
              ? '✓  Cleared for Travel'
              : '✗  Not Cleared — Further Assessment Required'}
        </Typography>
      </Box>

      {/* ── Body: fields + QR ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, px: 2.5, pt: 2, pb: 1.5 }}>

        {/* Field table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map(([label, value]) => (
                <Box component="tr" key={label} sx={{ borderBottom: '1px solid #eee' }}>
                  <Box component="td" sx={{
                    py: 0.7, pr: 1.5,
                    fontSize: '0.72rem',
                    color: '#666',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    width: '38%',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                  }}>
                    {label}
                  </Box>
                  <Box component="td" sx={{ py: 0.7, fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a', wordBreak: 'break-word' }}>
                    {value}
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
          <Box sx={{
            p: 1,
            borderRadius: 1.5,
            border: `2px solid ${accent}`,
            bgcolor: isCleared ? 'rgba(26,122,60,0.06)' : 'rgba(139,0,0,0.06)',
            width: 90,
            height: 90,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {qrValue ? (
              <QRCodeSVG
                id="public-pass-qr"
                value={qrValue}
                size={90}
                fgColor={accent}
                bgColor="#ffffff"
                level="H"
              />
            ) : (
              <Typography sx={{ fontSize: '0.65rem', color: '#999', textAlign: 'center', px: 1 }}>
                QR unavailable
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: '#888', textAlign: 'center', maxWidth: 110 }}>
            Scan to verify pass
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Footer ── */}
      <Box sx={{ px: 2.5, py: 1, bgcolor: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
          Issued by: UHAI
          {reportId ? `  ·  Ref: ${reportId}` : ''}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
          Valid only for the screening date shown · Not transferable
        </Typography>
      </Box>
    </Box>
  );
};

export default HealthPassCard;
